/**
 * Authentication API Service
 *
 * Handles all authentication-related API calls:
 * - Login
 * - Signup/Registration
 * - User types
 * - Profile updates
 * - Photo uploads
 */

import { post, get, uploadFile } from './client';
import { TOTP_ApiDetails } from '../../Constants';

export const authApi = {
    /**
     * User login
     * @param {Object} credentials
     * @param {string} credentials.email_address - User email
     * @param {string} credentials.password - User password
     * @param {number} credentials.user_type - User type ID
     * @returns {Promise<Object>} Login response with user data
     */
    login: async (credentials) => {
        return post('pegasus/visionary/authorization/login', credentials, {
            requiresAuth: false,
        });
    },

    /**
     * Create new user account
     * @param {Object} userData
     * @param {string} userData.first_name - First name
     * @param {string} userData.last_name - Last name
     * @param {string} userData.email_address - Email
     * @param {string} userData.password - Password
     * @param {string} userData.phone_number - Phone number
     * @param {number} userData.user_type - User type ID
     * @returns {Promise<Object>} Signup response
     */
    signup: async (userData) => {
        return post('pegasus/visionary/authorization/createAccount', userData, {
            requiresAuth: false,
        });
    },

    /**
     * Get all available user types
     * @returns {Promise<Object>} List of user types
     */
    getUserTypes: async () => {
        return get('pegasus/visionary/extras/user_types', {
            requiresAuth: false,
        });
    },

    /**
     * Update user profile
     * @param {Object} userData - Updated user data
     * @returns {Promise<Object>} Update response
     */
    updateProfile: async (userData) => {
        return post('pegasus/visionary/authorization/update', userData);
    },

    /**
     * Upload user profile photo
     * @param {string} landlordId - Landlord ID
     * @param {File} photoFile - Photo file to upload
     * @returns {Promise<Object>} Upload response
     */
    uploadPhoto: async (landlordId, photoFile) => {
        const formData = new FormData();
        formData.append('landlord_id', landlordId);
        formData.append('photo', photoFile);

        return uploadFile('pegasus/visionary/authorization/photo', formData);
    },

    /**
     * Get user profile photo
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} Photo data
     */
    getPhoto: async (landlordId) => {
        return post('pegasus/visionary/authorization/photo', {
            landlord_id: landlordId,
        });
    },
};

/**
 * Sky Auth API Service (2FA Authentication)
 */
export const skyAuthApi = {
    /**
     * Confirm 2FA authentication code
     * @param {Object} confirmData
     * @param {string} confirmData.auth_code - Authentication code
     * @param {string} confirmData.identifier_type - Type (e.g., "MAIL")
     * @param {string} confirmData.identifier - User identifier (email)
     * @param {number} confirmData.program_id - Program ID
     * @returns {Promise<Object>} Confirmation response
     */
    confirmAuthCode: async (confirmData) => {
        const response = await fetch(`${TOTP_ApiDetails}/sky-auth/confirmation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(confirmData),
        });

        const data = await response.json();
        return {
            data,
            status: response.status,
            ok: response.ok,
        };
    },

    /**
     * Add biometric authentication
     * @param {Object} biometricData
     * @param {string} biometricData.identifier_type - Type (e.g., "MAIL")
     * @param {string} biometricData.identifier - User identifier (email)
     * @param {number} biometricData.program_id - Program ID
     * @returns {Promise<Object>} Biometric add response
     */
    addBiometric: async (biometricData) => {
        const response = await fetch(`${TOTP_ApiDetails}/sky-auth/authorization/biometric/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(biometricData),
        });

        const data = await response.json();
        return {
            data,
            status: response.status,
            ok: response.ok,
        };
    },
};

export default authApi;
